import { assert, BaseNumber, NormalizedNumber, raise } from '@sparkdotfi/common-universal'
import { decodeEventLog, erc4626Abi, TransactionReceipt } from 'viem'

import { createDepositToSavingsActions } from '../../deposit-to-savings/logic/createDepositToSavingsActions'
import { createWithdrawFromSavingsActions } from '../../withdraw-from-savings/logic/createWithdrawFromSavingsActions'
import { UpgradeSusdcObjective } from '../types'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Action, ActionContext } from '@/features/actions/logic/types'

export function createUpgradeSusdcActions({ amount, susdc }: UpgradeSusdcObjective, context: ActionContext): Action[] {
  assert(context.tokenRepository, 'Token repository is required for upgrade susdc action')
  const usdc = context.tokenRepository.findOneBySymbol(TokenSymbol('USDC'))
  const spusdc = context.tokenRepository.findOneBySymbol(TokenSymbol('spUSDC'))

  const susdcConverter =
    context.savingsAccounts?.findOneBySavingsTokenSymbol(TokenSymbol('sUSDC'))?.converter ??
    raise('sUSDC converter is required for upgrade susdc action')

  const usdcWithdrawalAmount = (() => {
    const fallbackUsdcWithdrawalAmount = susdcConverter.convertToAssets({ shares: amount })
    const [, withdrawReceipt] = context.txReceipts.find(([action]) => action.type === 'withdrawFromSavings') ?? []
    return withdrawReceipt
      ? getUsdcWithdrawalAmountFromWithdrawReceipt(susdc, withdrawReceipt)
      : fallbackUsdcWithdrawalAmount
  })()

  return [
    ...createWithdrawFromSavingsActions(
      {
        type: 'withdrawFromSavings',
        token: usdc,
        savingsToken: susdc,
        amount,
        isRedeem: true,
        mode: 'withdraw',
        displayMode: 'savings',
      },
      context,
    ),
    ...createDepositToSavingsActions(
      {
        type: 'depositToSavings',
        token: usdc,
        savingsToken: spusdc,
        value: usdcWithdrawalAmount,
        displayMode: 'savings',
      },
      context,
    ),
  ]
}

function getUsdcWithdrawalAmountFromWithdrawReceipt(token: Token, receipt: TransactionReceipt): NormalizedNumber {
  for (const log of receipt.logs) {
    try {
      const decodedLog = decodeEventLog({
        abi: erc4626Abi,
        data: log.data,
        topics: log.topics,
      })
      if (decodedLog.eventName === 'Withdraw') {
        return token.fromBaseUnit(BaseNumber(decodedLog.args.assets))
      }
    } catch {
      // ignore error - there may be some other events we are not able to parse
    }
  }
  throw new Error('Withdraw receipt does not contain withdraw event')
}
